import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _typeof from "@babel/runtime/helpers/esm/typeof";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
/* eslint-disable max-len */
import { getServiceHost } from '@bfe/by-route-finder';
import { API_KEY_MAP, DEFAULT_LANG, REQUEST_ERROR_TOAST } from '../constants';

/**
 * 通过window是否存在，判断是否在客户端环境
 */
export var isClient = typeof window !== 'undefined';

/**
 * 添加事件上报
 * @param {Object} args 事件参数
 * @return {void}
 */
export var addEvent = function addEvent(eventName, props) {
  var _window$Monitor, _window$Monitor$log;
  return isClient ? (_window$Monitor = window.Monitor) === null || _window$Monitor === void 0 ? void 0 : (_window$Monitor$log = _window$Monitor.log) === null || _window$Monitor$log === void 0 ? void 0 : _window$Monitor$log.call(_window$Monitor, _objectSpread({
    t: eventName
  }, props)) : null;
};

/**
 * 获取默认语言
 */
export var getDefaultLang = function getDefaultLang() {
  return localStorage.getItem('BYBIT_LANG_KEY') || DEFAULT_LANG;
};

/**
 * 判断是否为对象
 *
 * @param {any} input 传入的参数
 */
export var isObject = function isObject(input) {
  return Object.prototype.toString.call(input) === '[object Object]';
};

/**
 * 删除对象中的指定键，不影响原对象
 * @param {Object} obj 对象
 * @param {Array} keys 要删除的键
 * @return {Object} 返回新对象
 */
export var omit = function omit(obj, keys) {
  // 如果不是对象或者为null，则直接返回
  if (_typeof(obj) !== 'object' || obj === null) {
    return obj;
  }

  // 使用 Set 进行键查找，比数组查找更高效
  var keysSet = new Set(keys);

  // 递归函数
  var omitHelper = function omitHelper(input) {
    // 如果不是对象或者为null，则直接返回
    if (_typeof(input) !== 'object' || input === null) {
      return input;
    }

    // 如果是数组，则遍历数组中的每个元素
    if (Array.isArray(input)) {
      return input.map(omitHelper);
    }

    // 创建一个新对象
    var newObj = {};

    // 遍历对象的每个键值对
    Object.keys(input).forEach(function (key) {
      if (!keysSet.has(key)) {
        newObj[key] = omitHelper(input[key]);
      }
    });

    // 返回新对象
    return newObj;
  };

  // 返回结果
  return omitHelper(obj);
};

/**
 * 交易对清洗+映射
 *
 * @param {Array} symbols 交易对数据
 * @return {Array} 清洗后的交易对数据
 */
export var transformSymbolsData = function transformSymbolsData(symbols) {
  if (!(symbols === null || symbols === void 0 ? void 0 : symbols.length)) return [];
  var apiKeyMapEntries = Object.entries(API_KEY_MAP);
  return symbols.map(function (item) {
    return _objectSpread(_objectSpread({}, item), {}, {
      quoteTokenSymbols: item.quoteTokenSymbols.map(function (quote) {
        return apiKeyMapEntries.reduce(function (acc, _ref) {
          var _ref2 = _slicedToArray(_ref, 2),
            shortKey = _ref2[0],
            fullKey = _ref2[1];
          if (shortKey) {
            acc[fullKey] = quote[shortKey];
          }
          return acc;
        }, {});
      })
    });
  });
};

/**
 * 清洗平铺quoteTokenSymbols
 * @param {Array} data 交易对数据
 * @return {Array} 返回quoteTokenSymbols数组
 */
export var getQuoteTokenSymbols = function getQuoteTokenSymbols(data) {
  return data.reduce(function (acc, item) {
    var _item$quoteTokenSymbo;
    if (item === null || item === void 0 ? void 0 : (_item$quoteTokenSymbo = item.quoteTokenSymbols) === null || _item$quoteTokenSymbo === void 0 ? void 0 : _item$quoteTokenSymbo.length) {
      // 遍历 item.quoteTokenSymbols，并为每个对象添加 tokenType 属性
      var updatedSymbols = item.quoteTokenSymbols.map(function (symbol) {
        return _objectSpread(_objectSpread({}, symbol), {}, {
          tokenType: item.tokenType
        });
      });
      acc.push.apply(acc, _toConsumableArray(updatedSymbols));
    }
    return acc;
  }, []);
};

/**
 * 格式化交易对数据，生成映射，key默认为symbolId
 * @param {Array} symbols 交易对数据
 * @return {Object} 返回格式化后的交易对数据
 */
export var formatSymbolsMap = function formatSymbolsMap() {
  var symbols = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var key = arguments.length > 1 ? arguments[1] : undefined;
  return symbols.reduce(function (map, item) {
    map[item[key] || item.symbolId] = item;
    return map;
  }, {});
};

/**
 * 获取quoteTokenSymbols映射
 * @param {Array} data 交易对数据
 * @return {Object} 返回quoteTokenSymbols映射
 */
export var getQuoteTokenSymbolsMap = function getQuoteTokenSymbolsMap(data) {
  var symbols = getQuoteTokenSymbols(data);
  return formatSymbolsMap(symbols);
};

/**
 * 合并两个数组对象
 * @param {Array} a 数组a
 * @param {Array} b 数组b
 * @param {Function} handleMissingKey 处理缺失key的回调
 * @param {String} key 唯一标识key
 * @return {Array} 返回合并后的数组
 */
export var mergeArrayObjects = function mergeArrayObjects() {
  var a = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var b = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var handleMissingKey = arguments.length > 2 ? arguments[2] : undefined;
  var key = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 'si';
  if (!Array.isArray(a) || !Array.isArray(b)) {
    return [];
  }
  var bMap = formatSymbolsMap(b, key);
  for (var i = 0; i < a.length; i += 1) {
    var aItem = a[i];
    var bItem = bMap[aItem[key]];
    if (bItem) {
      // 用 aItem 的 tfn 属性替换 bItem 中的 tfn 属性，因为 aItem 中的 tfn 属性是最新的
      bItem.tfn = aItem.tfn;

      // 合并对象
      Object.assign(aItem, bItem);
    } else {
      if (handleMissingKey) {
        handleMissingKey(aItem);
      }
      break;
    }
  }
  return a;
};

/**
 * 获取 API host
 * @return {string} 返回 API host
 */
export var getApi2host = function getApi2host() {
  // 为兼容webpack5，应避免对process.env进行解构
  // eslint-disable-next-line prefer-destructuring
  var BYBIT_API2_HOST = process.env.BYBIT_API2_HOST;
  return BYBIT_API2_HOST || getServiceHost().API2_HOST;
};

/**
 * 错误处理方法
 *
 * @param {Object} err 错误对象
 * @return {Object}
 */
export var error = function error(err) {
  var code = 500;
  var errMsg = REQUEST_ERROR_TOAST;
  try {
    if (err === null || err === void 0 ? void 0 : err.response) {
      var _res$request;
      var res = err.response;
      var data = res.data || {};
      code = data.ext_code || (res === null || res === void 0 ? void 0 : (_res$request = res.request) === null || _res$request === void 0 ? void 0 : _res$request.status) || (res === null || res === void 0 ? void 0 : res.status) || code;
      errMsg = data.ret_msg || data.error || errMsg;
    }
  } catch (e) {
    //
  }
  return {
    success: false,
    code: code,
    message: errMsg,
    result: null
  };
};

/**
 * 兼容接口返回的小驼峰 返回值，以及其msg 不支持中文，会用urlEncode编码，这里需要解码
 */
function adaptResponse(data) {
  if (!data) {
    return;
  }
  if (data.ret_code === undefined) {
    data.ret_code = data.retCode;
  }
  if (data.ret_msg === undefined) {
    try {
      data.ret_msg = decodeURIComponent(data.retMsg);
    } catch (_unused) {
      data.ret_msg = data.retMsg;
    }
  }
}

/**
 * 格式化响应数据
 *
 * @param {Object} res 响应数据
 * @return {Object} 返回响应数据结果对象
 */
export var transformResponse = function transformResponse(res) {
  var data = (res === null || res === void 0 ? void 0 : res.data) || res;
  adaptResponse(data);

  // 如果不是正常 JSON 数据则认为是错误
  if (!isObject(data)) {
    return error('Invalid response data');
  }
  // 封装后端 Response Schema
  data.success = data.ret_code === 0;
  data.code = data.ret_code;
  data.message = !data.ret_msg && data.ret_code !== 0 ? 'Access error, please try again later' : data.ret_msg;
  data.result = Object.prototype.hasOwnProperty.call(data, 'result') ? data.result : null;
  return data;
};

/**
 * 安全的 JSON.parse，避免传入的值不符合类型，抛出异常
 *
 * @param {any} variable JSON 字符串
 * @param {any} defaultValue 默认值
 */
export var safeJSONParse = function safeJSONParse(variable) {
  var defaultValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  try {
    return JSON.parse(variable);
  } catch (err) {
    return defaultValue;
  }
};

/**
 * 将变量转换为数组
 * @param {any} arr 变量
 * @param {Object} options 配置项
 * @param {boolean} options.ignoreNull 是否忽略 null 或 undefined，默认为 false
 * @return {Array} 返回数组
 */
export function toArray(arr) {
  var _ref3 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref3$ignoreNull = _ref3.ignoreNull,
    ignoreNull = _ref3$ignoreNull === void 0 ? false : _ref3$ignoreNull;
  if (ignoreNull && (arr === null || arr === undefined)) return [];
  return Array.isArray(arr) ? arr : [arr];
}

/**
 * 格式化精度，默认值为 `0`
 *
 * @param {string} value 后台配置的精度原始字段，如：0.0001 、0.1 、0
 * @return {number}
 */
export function formatPrecision(value) {
  if (typeof value !== 'string' || value.indexOf('.') === -1) return 0;
  var split = value.split('.');
  return split[1] ? split[1].length : 0;
}